import type React from 'react';
import { useEffect } from 'react';

import { Text } from '@change/ds-core-ui';
import { BaseSlider } from '@change/ds-forms';

import { useI18n, withI18nPlaceholders } from '@change-corgi/core-react/i18n';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/psf/share/sharing-goal-experiment.yml';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import { useCurrentUserId } from '#src/app/shared/hooks/session';

import { ShareFeed } from '../ShareFeed';

import { GoalPresetChips } from './components/GoalPresetChips';
import { GoalShareActions } from './components/GoalShareActions';
import { formatGoal, MAX_GOAL, MIN_GOAL } from './constants';
import { useReachGoalStep } from './hooks/useReachGoalStep';
import { useSetGoalChips } from './hooks/useSetGoalChips';
import { useSetGoalStep } from './hooks/useSetGoalStep';

type SliderGoalInputProps = {
	petitionId: string;
	userId: string | undefined;
	initialShareGoal: number | null;
};

/**
 * Own leaf component so its hook (useSetGoalStep) is called unconditionally -- inputVariant is
 * resolved once per SharingGoalFlow instance, but hooks can't be called conditionally on it.
 */
const SliderGoalInput: React.FC<SliderGoalInputProps> = ({ petitionId, userId, initialShareGoal }) => {
	const { translate, translatePlural } = useI18n();
	const { goalValue, setGoalValue, trackSliderChange, saveGoal, emoji, message } = useSetGoalStep({
		petitionId,
		userId,
		initialShareGoal,
	});

	return (
		<>
			<div className="mt-2 flex flex-col gap-2 px-4">
				<BaseSlider
					ariaLabel={translate('corgi.psf.share.sharing_goal.slider_aria_label')}
					value={goalValue}
					minValue={MIN_GOAL}
					maxValue={MAX_GOAL}
					step={1}
					onChange={setGoalValue}
					onChangeEnd={(value: number) => {
						trackSliderChange(value);
					}}
					showTooltip
					size="thick"
					tooltipTextSize="medium"
					formatValue={(value: number) =>
						translatePlural('corgi.psf.share.sharing_goal.slider_label', value, { count: formatGoal(value) })
					}
					data-qa="sharing-goal-slider"
					tooltipAlignment="follow"
				/>

				<Text variant="body" size="small" className="text-typography-weak">
					<span aria-hidden>{emoji}</span> {message}
				</Text>
			</div>

			<GoalShareActions onShareSuccess={saveGoal} />
		</>
	);
};

type ChipsGoalInputProps = {
	petitionId: string;
	userId: string | undefined;
};

const CHIPS_HEADLINE_ID = 'sharing-goal-chips-headline';

/**
 * Own leaf component so its hook (useSetGoalChips) is called unconditionally -- see SliderGoalInput.
 */
const ChipsGoalInput: React.FC<ChipsGoalInputProps> = ({ petitionId, userId }) => {
	const { translate } = useI18n();
	const { goalValue, selectGoal, saveGoal, emoji, message } = useSetGoalChips({ petitionId, userId });

	return (
		<>
			{/* -mt-4 (instead of SliderGoalInput's mt-2) pulls this closer to the shared header block --
			the outer gap-8/md:gap-9 above it is sized for the slider's emoji+message line, which sits
			further down; Figma's chips layout wants a tighter ~16px gap above the headline. */}
			<div className="-mt-4 flex flex-col gap-4 px-4">
				<Text
					as="p"
					variant="heading"
					size="x-small"
					fontWeight="bold"
					className="text-typography-information m-0 text-center"
					id={CHIPS_HEADLINE_ID}
					aria-live="polite"
				>
					{message == null ? (
						translate('corgi.psf.share.sharing_goal.chips.headline')
					) : (
						<>
							<span aria-hidden>{emoji}</span> {message}
						</>
					)}
				</Text>

				<GoalPresetChips value={goalValue} onChange={selectGoal} ariaLabelledBy={CHIPS_HEADLINE_ID} />
			</div>

			<GoalShareActions onShareSuccess={saveGoal} />
		</>
	);
};

export type SharingGoalInputVariant = 'slider' | 'chips' | 'none';

type Props = {
	inputVariant?: SharingGoalInputVariant;
};

/**
 * Wrapped below with i18n placeholders for the chips headline/motivation copy and the variant_2
 * no-slider body copy (both still subject to change while their arms are running -- only
 * productize a given key into corgi.en-US.yml if that arm wins).
 */
const SharingGoalFlowBase: React.FC<Props> = ({ inputVariant = 'slider' }) => {
	const { translate } = useI18n();
	const { data } = usePsfSharePageData();
	const petitionId = data.petition.id;
	const userId = useCurrentUserId();
	const {
		data: { userName, shareFeed },
	} = useReachGoalStep();

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	let goalInput: React.ReactNode;
	if (inputVariant === 'chips') {
		goalInput = <ChipsGoalInput petitionId={petitionId} userId={userId} />;
	} else if (inputVariant === 'none') {
		goalInput = <GoalShareActions />;
	} else {
		goalInput = <SliderGoalInput petitionId={petitionId} userId={userId} initialShareGoal={data.initialShareGoal} />;
	}

	return (
		<>
			<div className="flex flex-col gap-8 px-6 md:gap-9 md:px-10">
				<div className="flex flex-col gap-3 text-center">
					<div className="flex flex-col gap-2">
						<Text as="p" fontWeight="bold" className="text-typography-weak m-0">
							{translate('corgi.psf.share.sharing_goal.greeting', { user_name: userName })}
						</Text>
						<Text variant="heading" size="large" as="h1" className="text-typography-strong m-0">
							{translate('corgi.psf.share.sharing_goal.heading')}
						</Text>
					</div>
					<Text variant="body" className="text-typography-strong">
						{inputVariant === 'none'
							? translate('corgi.psf.share.sharing_goal.none.body')
							: translate('corgi.psf.share.sharing_goal.body')}
					</Text>
				</div>

				{goalInput}
			</div>

			{shareFeed.showFeed && (
				<div className="relative mt-7 h-full overflow-hidden md:mt-9" data-qa="share-feed">
					<ShareFeed items={shareFeed.items} />
				</div>
			)}
		</>
	);
};

export const SharingGoalFlow = withI18nPlaceholders(I18N_PLACEHOLDERS, SharingGoalFlowBase);
